import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, FileText, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { StaffAnswersRequest, STAFF_QUESTIONS } from '../../types/appeals';
import { decodeHtmlEntities } from '../../utils/decodeHtmlEntities';

interface StaffAnswersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (answers: StaffAnswersRequest) => Promise<void>;
  existingAnswers?: Partial<StaffAnswersRequest>;
}

function decodeStaffAnswers(ex?: Partial<StaffAnswersRequest>): StaffAnswersRequest {
  return {
    sq_rules_broken: decodeHtmlEntities(ex?.sq_rules_broken),
    sq_evidence: decodeHtmlEntities(ex?.sq_evidence),
    sq_prior_offenses: decodeHtmlEntities(ex?.sq_prior_offenses),
    sq_severity: decodeHtmlEntities(ex?.sq_severity),
    sq_community_harm: decodeHtmlEntities(ex?.sq_community_harm),
    sq_warning_given: decodeHtmlEntities(ex?.sq_warning_given),
    sq_user_response: decodeHtmlEntities(ex?.sq_user_response),
    sq_staff_interpretation: decodeHtmlEntities(ex?.sq_staff_interpretation),
    sq_duration_reasoning: decodeHtmlEntities(ex?.sq_duration_reasoning),
    sq_jury_exclusions: decodeHtmlEntities(ex?.sq_jury_exclusions),
  };
}

export const StaffAnswersModal = ({ isOpen, onClose, onSubmit, existingAnswers }: StaffAnswersModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const existingAnswersRef = useRef(existingAnswers);
  existingAnswersRef.current = existingAnswers;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<StaffAnswersRequest>(() => decodeStaffAnswers(existingAnswers));

  useEffect(() => {
    if (!isOpen) return;
    setAnswers(decodeStaffAnswers(existingAnswersRef.current));
  }, [isOpen]);

  useOnClickOutside(modalRef, onClose);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const missing = STAFF_QUESTIONS.filter(q => {
      if (q.field === 'sq_jury_exclusions') return false;
      return !answers[q.field]?.trim();
    });

    if (missing.length > 0) {
      setError(`Please fill in all required fields: ${missing.map(q => q.label).join(', ')}`);
      return;
    }

    const tooLong = STAFF_QUESTIONS.filter(q => (answers[q.field]?.length || 0) > 2000);
    if (tooLong.length > 0) {
      setError(`The following fields exceed 2000 characters: ${tooLong.map(q => q.label).join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(answers);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit answers');
    } finally {
      setIsSubmitting(false);
    }
  };

  const completedCount = STAFF_QUESTIONS.filter(q => {
    if (q.field === 'sq_jury_exclusions') return true;
    return answers[q.field]?.trim();
  }).length;

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-4xl"
      >
        <button
          onClick={onClose}
          className="modal-close z-10"
          title="Close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="modal-header">
          <div className="min-w-0">
            <h2 className="modal-title flex items-center gap-2">
              <FileText className="w-5 h-5 text-accent-400" />
              Staff Answers
            </h2>
            <p className="modal-subtitle tabular-nums">
              Complete all 10 questions. Progress: {completedCount}/{STAFF_QUESTIONS.length}
            </p>
          </div>
        </div>

        <div className="w-full bg-glass-100/10 rounded-full h-1.5 mb-4 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={STAFF_QUESTIONS.length} aria-valuenow={completedCount}>
          <div
            className="bg-accent-400 h-1.5 rounded-full transition-all"
            style={{ width: `${(completedCount / STAFF_QUESTIONS.length) * 100}%` }}
          />
        </div>

        <div className="modal-body space-y-5">
          {STAFF_QUESTIONS.map((q, index) => {
            const charCount = answers[q.field]?.length || 0;
            const isRequired = q.field !== 'sq_jury_exclusions';
            const isEmpty = isRequired && !answers[q.field]?.trim();

            return (
              <div key={q.field}>
                <label className="block text-xs font-medium text-glass-300 mb-1">
                  <span className="text-glass-500 tabular-nums mr-1">#{index + 1}</span>
                  {q.label}
                  {isRequired && <span className="text-red-400 ml-0.5">*</span>}
                </label>
                <p className="text-xs text-glass-500 mb-1.5">{q.description}</p>
                <textarea
                  className="glass-input w-full min-h-[80px] resize-y"
                  placeholder={q.description}
                  value={answers[q.field]}
                  onChange={(e) => setAnswers(prev => ({ ...prev, [q.field]: e.target.value }))}
                  maxLength={2000}
                />
                <div className="flex justify-between mt-1">
                  {isEmpty && (
                    <span className="text-xs text-yellow-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      <span>Required</span>
                    </span>
                  )}
                  <span className={`text-xs ml-auto tabular-nums ${charCount > 1800 ? 'text-yellow-400' : 'text-glass-500'}`}>
                    {charCount}/2000
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="alert alert-danger mt-4" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <div className="modal-footer">
          <button onClick={onClose} className="glass-button-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              'Submit Answers'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
